import { Box, Button, Checkbox, FormControl, InputLabel, ListItemText, Menu, MenuItem, OutlinedInput, Select, Tooltip, Typography } from "@mui/material";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import { exportUnbilledTasks, getUnbilledTasks } from "api/services/billing/invoices";
import { getAllUsersInOrganzaition } from "api/services/users";
import Table, { ColumnType } from "components/Table";
import Members from "components/Members";
import useTitle from "hooks/useTitle";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ResType } from "types";
import { getCurrentFormattedDateTime, getTitle } from "utils";
import SearchContainer from "components/SearchContainer";
import ValidateAccess from "components/ValidateAccess";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetFilters } from "redux/reducers/taskboardSlice";
import {
  resetState as resetInvoiceState,
  handleClientChange,
  handleAddTasksToParticular,
  handleExistingOtherParticular,
  handleFieldChange,
} from "redux/reducers/createInvoiceSlice";
import { snack } from "components/toast";
import { handleError } from "utils/handleError";
import PageWithPermission from "components/PageWithPermission";
import { Permissions } from "data/permissons";
import { formattedAmount } from "utils/formattedAmount";

import { SortDirection, TaskStatus } from "data/constants";
import sortHandler from "components/Table/SortHandler";
import { mulitipleClient, primaryclient } from "assets";
import { usePermissions } from "context/PermissionsProvider";
import AmountFormat from "components/AmountFormat";
import excelIcon from "./../../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import { getUnbilledTaskFinancialYears } from "api/services/organization";
import moment from "moment";
import Loader from "components/Loader";

const formatFinancialYear = (fy: string) => {
  const [start, end] = (fy || "").split("-");
  return start && end ? `${start}-${end.slice(-2)}` : fy;
};

const taskStatusColor: Record<string, string> = {
  todo: "#149ECD",
  in_progress: "#F7964F",
  on_hold: "#F2353C",
  under_review: "#673AB7",
  completed: "#8BC34A",
  terminated: "red",
};

const SELECT_ALL = "__SELECT_ALL__";

const UnbilledTasks = () => {
  useTitle(`Billing | Un-Billed Tasks`);

  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [selected, setSelected] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({ status: [], billingEntity: [], type: [], financialYear: [], members: [] });
  const [sort, setSort] = useState<any>({});
  const [dates, setDates] = useState({ fromDate: null, toDate: null });
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [financialYears, setFinancialYears] = useState<string[]>([]);

  const { data: users }: ResType = useQuery("users", getAllUsersInOrganzaition);

  let column: Array<ColumnType> = [
    {
      key: "client.displayName",
      title: "Clients/Clients Group",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (row: any) => (
        row.client ? (
          <Typography sx={{ display: "flex", alignItems: "center" }}>
            {row.client.displayName}
            {row.client.isPrimary && (
              <img
                src={primaryclient}
                width="22px"
                style={{ marginLeft: "10px", marginRight: "10px" }}
                alt="primary"
              />
            )}
          </Typography>
        ) : (
          <Typography sx={{ display: "flex", alignItems: "center" }}>
            {row.clientGroup?.displayName}
            <img
              src={mulitipleClient}
              width="22px"
              style={{ marginLeft: "10px", marginRight: "10px" }}
              alt="group"
            />
          </Typography>
        )
      )
    },
    {
      key: "financialYear",
      title: "FY",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (row: any) => formatFinancialYear(row.financialYear),
    },
    {
      key: "taskNumber",
      title: "Task ID",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (row: any) => (
        <Typography
          variant="body2"
          sx={{
            // color: "#1976d2",
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: 500,
            "&:hover": { color: "#1976d2" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
        >
          {row.taskNumber}
        </Typography>
      ),
    },
    {
      key: "name",
      title: "Task Name",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      width: "20%",
    },
    {
      key: "status",
      title: "Status",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (row: any) => (
        <Typography color={taskStatusColor[row?.status] || "grey.500"} sx={{ fontWeight: "bold" }}>
          {getTitle(row?.status)}
        </Typography>
      ),
    },
    {
      key: "statusUpdatedAt",
      title: "Status Updated At",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
      render: (item: any) => item?.statusUpdatedAt ? moment(item?.statusUpdatedAt).format("DD-MM-YYYY HH:mm") : "-"
    },
    {
      key: "members",
      title: "Assignee",
      render: (row: any) => (
        <Members
          data={row?.members?.map((item: any) => ({
            title: item?.fullName,
            src: item?.imageStorage?.fileUrl,
          }))}
        />
      ),
    },
    {
      key: "feeAmount",
      title: "Service Fee",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      render: (row: any) => <AmountFormat amount={formattedAmount(row.feeAmount || 0)} />
    },
    {
      key: "expenditure",
      title: "Expenses Incurred",
      render: (row: any) => (
        <AmountFormat
          amount={formattedAmount(
            row?.expenditure?.reduce((total: number, item: any) => total + Number(item?.amount || 0), 0)
          )}
        />
      ),
    },
    {
      key: "proformaInvoiceId",
      title: "Prof Inv #",
      render: (row: any) => row?.ProformaInvoice?.invoiceNumber || "-",
    },
  ];
  const [columns, setColumns] = useState(column);
  const dispatch = useDispatch();

  const { data: fyData } = useQuery(
    ["unbilled-task-financial-years"],
    getUnbilledTaskFinancialYears, {
      onSuccess: (res: any) => {
        setFinancialYears((res?.data || []).filter((year: any) => !!year));
      }
    }
  );

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, []);

  const { data, isLoading }: ResType = useQuery(
    ["unbilled-tasks", {
      offset: page * pageCount,
      limit: pageCount,
      search,
      status: filters.status,
      billingEntity: filters.billingEntity,
      type: filters.type,
      financialYear: filters.financialYear,
      members: filters.members,
      ...dates,
      sort
    }],
    getUnbilledTasks, {
    onError: (err) => {
      if (!err?.error) {
        snack.error(`An error occurred while fetching unbilled tasks`);
      }
    },
    enabled: permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS)
  }
  );

  const { mutate: exportTasks, isLoading: isExporting } = useMutation(exportUnbilledTasks, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.file?.data || res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = file;
      link.download = `ATOM Pro - Un-Billed Task ${getCurrentFormattedDateTime()}.xlsx`;
      link.click();
      snack.success("Unbilled tasks exported successfully");
    },
    onError: (err: any) => {
      snack.error(handleError(err));
    },
  });

  const handleExport = () => {
    if (isExporting) return;
    exportTasks({
      search,
      status: filters.status,
      billingEntity: filters.billingEntity,
      type: filters.type,
      financialYear: filters.financialYear,
      members: filters.members,
      ...dates,
      sort,
    });
  };

  const handleRowClick = (v: any) => {
    window.open(`/task-board/${v.id}#details`, "_blank")
  };

  const setSearchChange = (e) => {
    setSearch(e);
    setPage(0);
  };

  const handleDates = (value) => {
    setDates(value);
    setPage(0);
  }

  const handleFilterChange = (key: string, allOptions?: string[]) => (event: any) => {
    const { value } = event.target;
    let next = typeof value === "string" ? value.split(",") : value;
    // "Select All" toggles between every option and none
    if (allOptions && next.includes(SELECT_ALL)) {
      next = filters[key].length === allOptions.length ? [] : allOptions;
    }
    setFilters({ ...filters, [key]: next });
    setPage(0);
  };

  const statusOptions: string[] = Object.values(TaskStatus);
  const memberOptions: string[] = (users?.data || []).map((user: any) => user.id.toString());

  const renderSelectAll = (key: string, allOptions: string[]) => {
    const count = filters[key].length;
    return (
      <MenuItem value={SELECT_ALL}>
        <Checkbox
          checked={allOptions.length > 0 && count === allOptions.length}
          indeterminate={count > 0 && count < allOptions.length}
        />
        <ListItemText primary="Select All" primaryTypographyProps={{ fontWeight: "bold" }} />
      </MenuItem>
    );
  };

  const getTotalExpenditure = (expenditures: any[]) => {
    let sum = 0;
    (expenditures || []).forEach((item: any) => {
      if (item.taskExpenseType === "ADDITIONAL" && item.includeInInvoice) {
        sum += Number(item.amount) * 1;
      }
    });
    return sum;
  };

  const isSameClientSelection =
    selected.length > 0 &&
    new Set(selected.map((task: any) => task.client?.id ?? `group-${task.clientGroup?.id}`)).size === 1;

  const hasExistingProformaInvoice = selected.some((task: any) => task.proformaInvoiceId);

  const handleGenerate = (type: "INVOICE" | "PROFORMA") => {
    setActionsAnchorEl(null);
    if (!isSameClientSelection) return;

    dispatch(resetInvoiceState());
    dispatch(handleClientChange({ client: selected[0].client || selected[0].clientGroup }));
    if (type === "PROFORMA") {
      dispatch(handleFieldChange({ key: "type", value: "PROFORMA" }));
    }

    const preparedTasks = selected.map((task: any) => {
      const additionalFee = getTotalExpenditure(task.expenditure);
      return {
        id: task.id,
        taskNumber: task.taskNumber,
        taskName: task.name,
        expectedFee: Number(task.feeAmount) || 0,
        actualFee: (Number(task.feeAmount) || 0) + additionalFee,
        additionalFee,
        status: task.status,
      };
    });

    if (preparedTasks.length > 1) {
      const totalRate = preparedTasks.reduce((sum, t) => sum + t.actualFee, 0);
      dispatch(handleAddTasksToParticular([
        { taskItemType: "MULTIPLE_LINE", feeAmount: totalRate, tasks: preparedTasks },
      ]));
    } else {
      dispatch(handleAddTasksToParticular(
        preparedTasks.map((t) => ({ ...t, feeAmount: t.actualFee, taskItemType: "SINGLE_LINE" }))
      ));
    }

    let otherParticularIndex = 0;
    selected.forEach((task: any) => {
      (task.expenditure || []).forEach((item: any) => {
        if (item.taskExpenseType !== "ADDITIONAL" && item.includeInInvoice) {
          dispatch(handleExistingOtherParticular({
            id: item.id,
            taskExpenseType: item.taskExpenseType,
            index: otherParticularIndex++,
            key: item.particularName,
            value: Number(item.amount),
          }));
        }
      });
    });

    dispatch(handleFieldChange({ key: "fromTask", value: true }));
    navigate(type === "PROFORMA" ? "/billing/proforma/add" : "/billing/invoices/add");
  };

  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };

  const totalCount = data?.data?.totalCount || 0;

  // if(isLoading) return <Loader />;

  return (
    <Box p={3} pl={1} pr={1} pb={0}>
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" mb={1.5}>
          <SearchContainer
            debounced
            value={search}
            placeHolder="Search by Client/Clients Group | Task ID | Task Name"
            onChange={setSearchChange}
          />
          <Box ml="auto" display={"flex"} flexDirection={"row"} gap={2}>
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>Financial Year</InputLabel>
            <Select
              multiple
              value={filters.financialYear}
              onChange={handleFilterChange("financialYear", financialYears)}
              input={<OutlinedInput label="Financial Year" />}
              renderValue={(selected: string[]) => selected.map(formatFinancialYear).join(", ")}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {renderSelectAll("financialYear", financialYears)}
              {financialYears.map((year: string) => (
                <MenuItem key={year} value={year}>
                  <Checkbox checked={filters.financialYear.includes(year)} />
                  <ListItemText primary={formatFinancialYear(year)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>Task Status</InputLabel>
            <Select
              multiple
              value={filters.status}
              onChange={handleFilterChange("status", statusOptions)}
              input={<OutlinedInput label="Task Status" />}
              renderValue={(selected: string[]) => selected.map((v) => getTitle(v)).join(", ")}
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {renderSelectAll("status", statusOptions)}
              {statusOptions.map((status: string) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={filters.status.includes(status)} />
                  <Typography color={taskStatusColor[status] || "grey.500"} sx={{ fontWeight: "bold" }}>
                    {getTitle(status)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              multiple
              value={filters.members}
              onChange={handleFilterChange("members", memberOptions)}
              input={<OutlinedInput label="Members" />}
              renderValue={(selected: string[]) =>
                users?.data
                  ?.filter((user: any) => selected.includes(user.id.toString()))
                  .map((user: any) => user.fullName)
                  .join(", ")
              }
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {renderSelectAll("members", memberOptions)}
              {users?.data?.map((user: any) => (
                <MenuItem key={user.id} value={user.id.toString()}>
                  <Checkbox checked={filters.members.includes(user.id.toString())} />
                  <ListItemText primary={user.fullName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

            <Button
              endIcon={<ArrowDropDownOutlinedIcon />}
              color="primary"
              variant="outlined"
              disabled={!isSameClientSelection}
              onClick={(e) => setActionsAnchorEl(e.currentTarget)}
              sx={{ maxHeight: "40px" }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={Boolean(actionsAnchorEl)}
              onClose={() => setActionsAnchorEl(null)}
            >
              <ValidateAccess name={Permissions.CREATE_PROFORMA_INVOICE}>
                <MenuItem disabled={hasExistingProformaInvoice} onClick={() => handleGenerate("PROFORMA")}>+ Proforma Invoice</MenuItem>
              </ValidateAccess>
              <ValidateAccess name={Permissions.CREATE_INVOICE}>
                <MenuItem onClick={() => handleGenerate("INVOICE")}>+ Invoice</MenuItem>
              </ValidateAccess>
            </Menu>
            <Tooltip title={isExporting ? "Exporting..." : "Data Export to Excel"}>
              <Button onClick={handleExport} disabled={isExporting} sx={{ minWidth: 40, maxHeight: "40px" }}>
                <img src={excelIcon} alt="Export" style={{ width: 30, height: 30, opacity: isExporting ? 0.5 : 1 }} />
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <Table
          sx={{ pb: 0, mb: 0, height: '505px' }}
          selection={{ selected, setSelected }}
          pagination={{ totalCount, pageCount, setPageCount, page, setPage }}
          data={data?.data?.result || []}
          columns={columns}
          loading={isLoading}
          sortHandler={handleSort}
        />
    </Box>
  );
};



export default UnbilledTasks;

